import React, { useState, useEffect } from 'react';
import {
    IonModal,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonButton,
    IonContent,
    IonSpinner,
    IonIcon,
    useIonToast
} from '@ionic/react';
import { closeOutline, printOutline, saveOutline, trashOutline } from 'ionicons/icons';
import { ShippingBooking, ShippingBookingItem } from '../../types/shipping-booking.type';
import { updateAdminShippingBooking, deleteAdminShippingBooking } from '../../services/shipping-booking.service';
import { useAppContext } from '../../services/app-context.service';
import { printShippingChallan } from '../../services/print.service';
import './AdminShippingBookingModal.scss';

const BDT = new Intl.NumberFormat('en-IN');

const statusOptions = [
    'Pending',
    'Received in China Warehouse',
    'Shipped To BD',
    'BD Warehouse',
    'Out for Delivery',
    'Delivered',
    'Cancelled'
];

interface Props {
    booking: ShippingBooking | null;
    isOpen: boolean;
    onClose: () => void;
    onUpdated: () => void;
}

const AdminShippingBookingModal: React.FC<Props> = ({ booking, isOpen, onClose, onUpdated }) => {
    const { settings } = useAppContext();
    const [presentToast] = useIonToast();
    const [activeTab, setActiveTab] = useState<'challan' | 'customer'>('challan');
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // Form states
    const [formData, setFormData] = useState<Partial<ShippingBooking>>({});
    const [items, setItems] = useState<ShippingBookingItem[]>([]);

    useEffect(() => {
        if (booking) {
            setFormData({ ...booking });
            setItems(booking.items && Array.isArray(booking.items) ? [...booking.items] : []);
        }
    }, [booking]);

    if (!booking) return null;

    const handleFieldChange = (field: keyof ShippingBooking, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Item changes
    const handleItemChange = (index: number, field: keyof ShippingBookingItem, value: any) => {
        const updated = [...items];
        updated[index] = { ...updated[index], [field]: value };
        setItems(updated);
    };

    const addItem = () => {
        setItems([...items, { item_name: '', quantity: 1, is_special_category: false }]);
    };

    const removeItem = (index: number) => {
        setItems(items.filter((_, i) => i !== index));
    };

    // Dynamic Challan calculations
    const weight = Math.max(0, Number(formData.weight ?? booking.weight));
    const ratePerKg = Math.max(0, Number(formData.shipment_rate_per_kg ?? booking.shipment_rate_per_kg));
    const shipmentCharge = weight * ratePerKg;
    const warehouseCharge = Math.max(0, Number(formData.warehouse_repackaging_cost ?? booking.warehouse_repackaging_cost));
    const transportCharge = Math.max(0, Number(formData.transportation_cost ?? booking.transportation_cost));
    const codCharge = Math.max(0, Number(formData.cod_charge ?? booking.cod_charge));
    const totalCost = warehouseCharge + shipmentCharge + transportCharge + codCharge;
    const paid = Math.max(0, Number(formData.paid ?? booking.paid));
    const due = Math.max(0, totalCost - paid);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const payload = {
                ...formData,
                items,
                warehouse_repackaging_cost: warehouseCharge,
                shipment_rate_per_kg: ratePerKg,
                transportation_cost: transportCharge,
                cod_charge: codCharge,
                weight,
                paid
            };

            await updateAdminShippingBooking(booking.id, payload);
            presentToast({
                message: 'Booking updated successfully. Movement SMS sent if status changed.',
                duration: 3000,
                color: 'success'
            });
            onUpdated();
            onClose();
        } catch (err: any) {
            console.error(err);
            presentToast({
                message: err.response?.data?.message || 'Failed to update booking',
                duration: 3500,
                color: 'danger'
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm(`Are you sure you want to permanently delete booking #${booking.booking_id}?`)) {
            return;
        }
        setIsDeleting(true);
        try {
            await deleteAdminShippingBooking(booking.id);
            presentToast({
                message: `Booking #${booking.booking_id} deleted successfully.`,
                duration: 2500,
                color: 'warning'
            });
            onUpdated();
            onClose();
        } catch (err: any) {
            console.error(err);
            presentToast({
                message: 'Failed to delete booking',
                duration: 3500,
                color: 'danger'
            });
        } finally {
            setIsDeleting(false);
        }
    };

    const handlePrint = () => {
        printShippingChallan({
            ...booking,
            ...formData,
            items,
            warehouse_repackaging_cost: warehouseCharge,
            shipment_rate_per_kg: ratePerKg,
            transportation_cost: transportCharge,
            cod_charge: codCharge,
            weight,
            paid
        }, settings);
    };

    return (
        <IonModal isOpen={isOpen} onDidDismiss={onClose}>
            <IonHeader className="no-print">
                <IonToolbar color="light">
                    <IonTitle>Booking #{booking.booking_id}</IonTitle>
                    <IonButtons slot="end">
                        <IonButton color="danger" fill="clear" onClick={handleDelete} disabled={isDeleting}>
                            <IonIcon icon={trashOutline} slot="start" />
                            Delete
                        </IonButton>
                        <IonButton onClick={handlePrint} color="primary">
                            <IonIcon icon={printOutline} slot="start" />
                            Print Challan
                        </IonButton>
                        <IonButton onClick={onClose}>
                            <IonIcon icon={closeOutline} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent className="ion-padding admin-booking-modal">
                <div className="modal-tab-nav no-print">
                    <button
                        className={activeTab === 'challan' ? 'active' : ''}
                        onClick={() => setActiveTab('challan')}
                    >
                        Challan &amp; Movement / SMS
                    </button>
                    <button
                        className={activeTab === 'customer' ? 'active' : ''}
                        onClick={() => setActiveTab('customer')}
                    >
                        Customer Options (All Editable)
                    </button>
                </div>

                {activeTab === 'challan' ? (
                    <div>
                        {/* Status Movement Selection Bar */}
                        <div className="challan-edit-charges no-print" style={{ marginBottom: 20 }}>
                            <div className="modal-grid-row">
                                <div className="col-half">
                                    <label style={{ display: 'block', fontWeight: 700, marginBottom: 6, color: '#0f172a' }}>
                                        Order Movement / Status:
                                    </label>
                                    <select
                                        className="status-select-badge"
                                        value={formData.status ?? booking.status}
                                        onChange={(e) => handleFieldChange('status', e.target.value)}
                                    >
                                        {statusOptions.map((st, i) => (
                                            <option key={i} value={st}>{st}</option>
                                        ))}
                                    </select>
                                    <small style={{ color: '#0d9488', marginTop: 4, display: 'block' }}>
                                        &bull; Changing status sends movement SMS to customer (CHB style)
                                    </small>
                                </div>
                                <div className="col-half">
                                    <label style={{ display: 'block', fontWeight: 700, marginBottom: 6, color: '#0f172a' }}>
                                        Admin Internal Notes:
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        style={{ width: '100%', padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6 }}
                                        placeholder="Admin remarks"
                                        value={formData.admin_notes ?? ''}
                                        onChange={(e) => handleFieldChange('admin_notes', e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Editable Challan Subjects (as per handwriting page 2 item 3) */}
                        <div className="challan-edit-charges no-print">
                            <h4>Challan Charges Adjustment</h4>

                            <div className="charge-row">
                                <label>a) Warehouse / Repackaging (BDT):</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={formData.warehouse_repackaging_cost ?? 0}
                                    onChange={(e) => handleFieldChange('warehouse_repackaging_cost', parseFloat(e.target.value) || 0)}
                                />
                            </div>

                            <div className="charge-row">
                                <label>b) Weight (KG):</label>
                                <input
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    value={formData.weight ?? 5}
                                    onChange={(e) => handleFieldChange('weight', parseFloat(e.target.value) || 0)}
                                />
                            </div>

                            <div className="charge-row">
                                <label>b) per kg Shipment Rate (BDT):</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={formData.shipment_rate_per_kg ?? 850}
                                    onChange={(e) => handleFieldChange('shipment_rate_per_kg', parseFloat(e.target.value) || 0)}
                                />
                            </div>

                            <div className="charge-row">
                                <label>c) Transportation / Courier (BDT):</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={formData.transportation_cost ?? 0}
                                    onChange={(e) => handleFieldChange('transportation_cost', parseFloat(e.target.value) || 0)}
                                />
                            </div>

                            <div className="charge-row">
                                <label>d) COD Charge (BDT):</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={formData.cod_charge ?? 0}
                                    onChange={(e) => handleFieldChange('cod_charge', parseFloat(e.target.value) || 0)}
                                />
                            </div>

                            <div className="charge-row">
                                <label>Paid Amount (BDT):</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={formData.paid ?? 0}
                                    onChange={(e) => handleFieldChange('paid', parseFloat(e.target.value) || 0)}
                                />
                            </div>
                        </div>

                        {/* Printable Challan Box */}
                        <div className="challan-box challan-print-area">
                            <div className="challan-header">
                                <div className="company-info">
                                    <h2>China Hub BD</h2>
                                    <p>{settings?.company_address || '151/1/F Ahamednagor, Zonaki Road, Mirpur-1, Dhaka'}</p>
                                    <p>Phone: {settings?.company_phone || '+8801709339369'} | Email: {settings?.company_email || 'infochinahubbd@gmail.com'}</p>
                                </div>
                                <div className="challan-title-box">
                                    <h3>SHIPPING CHALLAN</h3>
                                    <div><strong>Challan #:</strong> {booking.booking_id}</div>
                                    <div><strong>Date:</strong> {new Date(booking.created_at || Date.now()).toLocaleDateString()}</div>
                                    <div><strong>Status:</strong> {formData.status ?? booking.status}</div>
                                </div>
                            </div>

                            <div className="challan-meta-grid">
                                <div className="meta-item">
                                    <strong>Customer Name:</strong>
                                    <span>{formData.customer_name ?? booking.customer_name}</span>
                                </div>
                                <div className="meta-item">
                                    <strong>Phone:</strong>
                                    <span>{formData.customer_phone ?? booking.customer_phone}</span>
                                </div>
                                <div className="meta-item">
                                    <strong>Shipping Mark:</strong>
                                    <span>{formData.shipping_mark ?? booking.shipping_mark}</span>
                                </div>
                                <div className="meta-item">
                                    <strong>Method:</strong>
                                    <span>{formData.method ?? booking.method}</span>
                                </div>
                                <div className="meta-item">
                                    <strong>Tracking Number:</strong>
                                    <span>{formData.tracking_no ?? booking.tracking_no}</span>
                                </div>
                                <div className="meta-item">
                                    <strong>Cartons:</strong>
                                    <span>{formData.carton_count ?? booking.carton_count}</span>
                                </div>
                                <div className="meta-item">
                                    <strong>Packaging Size:</strong>
                                    <span>{formData.packaging_size || 'N/A'}</span>
                                </div>
                                <div className="meta-item">
                                    <strong>Route:</strong>
                                    <span>{formData.shipping_route || 'Guangzhou'}</span>
                                </div>
                            </div>

                            {/* Items in Carton */}
                            {items.length > 0 && (
                                <table className="challan-table">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Item Name</th>
                                            <th>Quantity</th>
                                            <th>Special Category (Battery/Liquid/Powder)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.map((it, idx) => (
                                            <tr key={idx}>
                                                <td>{idx + 1}</td>
                                                <td>{it.item_name}</td>
                                                <td>{it.quantity}</td>
                                                <td>{it.is_special_category ? 'Yes (Special)' : 'General'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}

                            {/* Subjects Charges Breakdown */}
                            <table className="challan-table">
                                <thead>
                                    <tr>
                                        <th>Subject</th>
                                        <th>Details</th>
                                        <th className="amount-col">Amount (BDT)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>a) Warehouse / Repackaging</td>
                                        <td>Handling &amp; Repackaging Charge</td>
                                        <td className="amount-col">{BDT.format(warehouseCharge)}/=</td>
                                    </tr>
                                    <tr>
                                        <td>b) Shipment Cost</td>
                                        <td>per kg Shipment {ratePerKg} x {weight} kg</td>
                                        <td className="amount-col">{BDT.format(shipmentCharge)}/=</td>
                                    </tr>
                                    <tr>
                                        <td>c) Transportation / Courier</td>
                                        <td>Local Courier / Delivery</td>
                                        <td className="amount-col">{BDT.format(transportCharge)}/=</td>
                                    </tr>
                                    <tr>
                                        <td>d) COD Charge</td>
                                        <td>Cash on Delivery Handling</td>
                                        <td className="amount-col">{BDT.format(codCharge)}/=</td>
                                    </tr>
                                </tbody>
                            </table>

                            {/* Totals */}
                            <table className="challan-totals-table">
                                <tbody>
                                    <tr className="total-row">
                                        <td>Total Bill:</td>
                                        <td className="amount-col">{BDT.format(totalCost)}/=</td>
                                    </tr>
                                    <tr>
                                        <td>Paid Amount:</td>
                                        <td className="amount-col">{BDT.format(paid)}/=</td>
                                    </tr>
                                    <tr className="due-row">
                                        <td>Total Due:</td>
                                        <td className="amount-col">{BDT.format(due)}/=</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    /* Tab 2: Customer Options (All Editable by Admin) */
                    <div className="challan-edit-charges">
                        <h4>Edit Customer Side Information</h4>

                        <div className="modal-grid-row">
                            <div className="col-half">
                                <label style={{ display: 'block', fontWeight: 600, fontSize: '0.88rem', marginBottom: 4 }}>
                                    Method:
                                </label>
                                <select
                                    className="form-control"
                                    style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #cbd5e1' }}
                                    value={formData.method ?? booking.method}
                                    onChange={(e) => handleFieldChange('method', e.target.value)}
                                >
                                    <option value="Air Shipment">Air Shipment</option>
                                    <option value="Sea Shipment">Sea Shipment</option>
                                </select>
                            </div>

                            <div className="col-half">
                                <label style={{ display: 'block', fontWeight: 600, fontSize: '0.88rem', marginBottom: 4 }}>
                                    Shipping Mark:
                                </label>
                                <input
                                    type="text"
                                    style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #cbd5e1' }}
                                    value={formData.shipping_mark ?? booking.shipping_mark}
                                    onChange={(e) => handleFieldChange('shipping_mark', e.target.value)}
                                />
                            </div>

                            <div className="col-half">
                                <label style={{ display: 'block', fontWeight: 600, fontSize: '0.88rem', marginBottom: 4 }}>
                                    Tracking Number:
                                </label>
                                <input
                                    type="text"
                                    style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #cbd5e1' }}
                                    value={formData.tracking_no ?? booking.tracking_no}
                                    onChange={(e) => handleFieldChange('tracking_no', e.target.value)}
                                />
                            </div>

                            <div className="col-half">
                                <label style={{ display: 'block', fontWeight: 600, fontSize: '0.88rem', marginBottom: 4 }}>
                                    Carton Count:
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #cbd5e1' }}
                                    value={formData.carton_count ?? booking.carton_count}
                                    onChange={(e) => handleFieldChange('carton_count', parseInt(e.target.value) || 1)}
                                />
                            </div>

                            <div className="col-half">
                                <label style={{ display: 'block', fontWeight: 600, fontSize: '0.88rem', marginBottom: 4 }}>
                                    Weight (KG):
                                </label>
                                <input
                                    type="number"
                                    step="0.1"
                                    style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #cbd5e1' }}
                                    value={formData.weight ?? booking.weight}
                                    onChange={(e) => handleFieldChange('weight', parseFloat(e.target.value) || 0)}
                                />
                            </div>

                            <div className="col-half">
                                <label style={{ display: 'block', fontWeight: 600, fontSize: '0.88rem', marginBottom: 4 }}>
                                    Packaging Size:
                                </label>
                                <input
                                    type="text"
                                    style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #cbd5e1' }}
                                    value={formData.packaging_size ?? ''}
                                    onChange={(e) => handleFieldChange('packaging_size', e.target.value)}
                                />
                            </div>

                            <div className="col-half">
                                <label style={{ display: 'block', fontWeight: 600, fontSize: '0.88rem', marginBottom: 4 }}>
                                    Shipping Route:
                                </label>
                                <select
                                    style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #cbd5e1' }}
                                    value={formData.shipping_route ?? 'Guangzhou'}
                                    onChange={(e) => handleFieldChange('shipping_route', e.target.value)}
                                >
                                    <option value="Guangzhou">Guangzhou (Gunzu)</option>
                                    <option value="Hong Kong">Hong Kong (Honkong)</option>
                                </select>
                            </div>

                            <div className="col-half">
                                <label style={{ display: 'block', fontWeight: 600, fontSize: '0.88rem', marginBottom: 4 }}>
                                    Customer Name:
                                </label>
                                <input
                                    type="text"
                                    style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #cbd5e1' }}
                                    value={formData.customer_name ?? booking.customer_name}
                                    onChange={(e) => handleFieldChange('customer_name', e.target.value)}
                                />
                            </div>

                            <div className="col-half">
                                <label style={{ display: 'block', fontWeight: 600, fontSize: '0.88rem', marginBottom: 4 }}>
                                    Customer Phone:
                                </label>
                                <input
                                    type="tel"
                                    style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #cbd5e1' }}
                                    value={formData.customer_phone ?? booking.customer_phone}
                                    onChange={(e) => handleFieldChange('customer_phone', e.target.value)}
                                />
                            </div>

                            <div className="col-full">
                                <label style={{ display: 'block', fontWeight: 600, fontSize: '0.88rem', marginBottom: 4 }}>
                                    Customer Address:
                                </label>
                                <textarea
                                    rows={2}
                                    style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #cbd5e1' }}
                                    value={formData.customer_address ?? ''}
                                    onChange={(e) => handleFieldChange('customer_address', e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Items in Carton Editor */}
                        <div style={{ marginTop: 20 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                <strong>Items Details of Carton</strong>
                                <button
                                    type="button"
                                    onClick={addItem}
                                    style={{ background: '#0a6670', color: '#fff', border: 'none', padding: '5px 12px', borderRadius: 4, cursor: 'pointer' }}
                                >
                                    + Add Item
                                </button>
                            </div>

                            {items.map((it, idx) => (
                                <div key={idx} style={{ display: 'flex', gap: 10, alignItems: 'center', background: '#ffffff', padding: 8, borderRadius: 6, marginBottom: 8, border: '1px solid #e2e8f0' }}>
                                    <input
                                        type="text"
                                        placeholder="Item name"
                                        style={{ flex: 3, padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 4 }}
                                        value={it.item_name}
                                        onChange={(e) => handleItemChange(idx, 'item_name', e.target.value)}
                                    />
                                    <input
                                        type="number"
                                        min="1"
                                        placeholder="Qty"
                                        style={{ width: 80, padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 4 }}
                                        value={it.quantity}
                                        onChange={(e) => handleItemChange(idx, 'quantity', parseInt(e.target.value) || 1)}
                                    />
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.82rem' }}>
                                        <input
                                            type="checkbox"
                                            checked={it.is_special_category}
                                            onChange={(e) => handleItemChange(idx, 'is_special_category', e.target.checked)}
                                        />
                                        Special (Liquid/Battery/Powder)
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => removeItem(idx)}
                                        style={{ background: '#fee2e2', color: '#dc2626', border: 'none', padding: '6px 10px', borderRadius: 4, cursor: 'pointer' }}
                                    >
                                        &times;
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Save Changes Floating / Fixed Button */}
                <div style={{ marginTop: 24, textAlign: 'right' }} className="no-print">
                    <IonButton color="medium" fill="outline" onClick={onClose} style={{ marginRight: 10 }}>
                        Cancel
                    </IonButton>
                    <IonButton color="primary" onClick={handleSave} disabled={isSaving}>
                        {isSaving ? <IonSpinner name="dots" /> : (
                            <>
                                <IonIcon icon={saveOutline} slot="start" />
                                Save Changes &amp; Notify SMS
                            </>
                        )}
                    </IonButton>
                </div>
            </IonContent>
        </IonModal>
    );
};

export default AdminShippingBookingModal;

